import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ImpersonationStopRow } from "@/components/layout/ImpersonationStopRow";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import apiClient from "@/lib/api/apiClient";
import { API_ENDPOINTS } from "@/lib/api/config";
import { ROLE_RENTER } from "@/types/auth";
import { normalizeHelpAboutHtmlLayout } from "@/features/applicant/utils/helpAboutHtml";
import type { AboutContentResponse } from "@/features/applicant/types/help";

/**
 * Company/PMC roles - same as Angular help-about.component (user-access.ts company_roles).
 * Used to show PMC/Property Help/About content instead of default.
 */
const COMPANY_ROLES = [
  "ROLE_PROPERTY_MANAGER",
  "ROLE_PROPERTY_AGENT",
  "ROLE_PROPERTY_ADMIN",
  "ROLE_COMPANY_MANAGER",
  "ROLE_COMPANY_ADMIN",
  "ROLE_REGIONAL_MANAGER",
];


/** Shared styles for CMS HTML: spacing, touch targets, footer/IP lines (API + fallback). */
const HELP_HTML_PROSE =
  [
    "help-about-html prose prose-sm sm:prose-base max-w-none w-full mx-0 text-left text-slate-800",
    /* Force left alignment — CMS often ships text-align:center / align=center */
    "[&_*]:!text-left",
    "[&_p]:!text-left [&_div]:!text-left [&_span]:!text-left [&_h1]:!text-left [&_h2]:!text-left [&_h3]:!text-left",
    "[&_li]:!text-left [&_ul]:!text-left [&_ol]:!text-left",
    "[&_button]:!text-center",
    "[&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-slate-700",
    "[&_strong]:font-semibold [&_strong]:text-slate-900",
    "[&_a]:font-medium [&_a]:text-blue-600 [&_a:hover]:text-blue-700 [&_a]:underline underline-offset-2",
    /* Clear gap after Privacy / text blocks before primary buttons (thumb-friendly spacing) */
    "[&_p+button]:mt-8 [&_p+button]:block",
    "[&_a+button]:mt-8 [&_a+button]:block",
    "[&_div+button]:mt-8",
    "[&_br+button]:mt-8",
    "[&_button]:inline-flex [&_button]:items-center [&_button]:justify-center",
    "[&_button]:min-h-[44px] [&_button]:touch-manipulation [&_button]:rounded-xl [&_button]:border-0",
    "[&_button]:bg-blue-600 [&_button]:px-5 [&_button]:py-2.5 sm:[&_button]:px-6 [&_button]:font-semibold [&_button]:text-white",
    "[&_button]:shadow-sm [&_button]:transition-colors [&_button]:active:bg-blue-800 [&_button]:hover:bg-blue-700",
    "[&_button]:w-full sm:[&_button]:w-auto sm:[&_button]:min-w-[min(100%,280px)]",
    "[&_button+button]:mt-3",
    /* IP / trademark lines (often <em> or <i> in CMS) */
    "[&_em]:text-sm [&_em]:leading-relaxed [&_em]:text-slate-600 [&_em]:italic",
    "[&_i]:text-sm [&_i]:leading-relaxed [&_i]:text-slate-600 [&_i]:italic",
    /* Copyright / closing line */
    "[&_p:last-child]:text-sm [&_p:last-child]:text-slate-500 [&_p:last-child]:font-normal [&_p:last-child]:not-italic",
  ].join(" ");

/** Static fallback when API fails or user is not authenticated (e.g. applicant flow). */
function StaticFallback() {
  return (
    <div data-testid="applicant-help-static-fallback" className="space-y-6 max-w-none text-left">
      <p className="text-slate-700 leading-relaxed text-base text-left">
        We couldn&apos;t load Help content. Please try again, or email{" "}
        <a
          className="text-blue-600 hover:text-blue-800 underline"
          href="mailto:help@libertyrent.com"
        >
          help@libertyrent.com
        </a>
        .
      </p>
      <div className="pb-6 border-b border-slate-200">
        <p className="text-sm text-slate-700">
          <span className="font-semibold">Privacy Policy:</span>{" "}
          <a
            data-testid="applicant-help-privacy-policy-link"
            href="https://www.libertyrent.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition-colors"
          >
            Click here
          </a>
        </p>
      </div>
      <div className="pb-6 border-b border-slate-200">
        <p className="text-sm text-slate-700">
          For other Questions or Enhancement Requests, please email us at:{" "}
          <a
            data-testid="applicant-help-email-link"
            href="mailto:help@libertyrent.com"
            className="text-blue-600 hover:text-blue-800 underline transition-colors"
          >
            help@libertyrent.com
          </a>
        </p>
      </div>
      <p className="text-sm italic leading-relaxed text-slate-600">
        The Cornerstone platform is protected intellectual property of Liberty Rent, LLC
      </p>
      <p className="text-sm text-slate-500 font-normal not-italic tracking-normal">
        © {new Date().getFullYear()} Liberty Rent, LLC. - all rights reserved
      </p>
    </div>
  );
}

export function Help() {
  const user = useAppSelector(selectUser);
  const roleName = user?.current_role?.role_name ?? "";

  const [contentLoading, setContentLoading] = useState(true);
  const [error, setError] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Help / About";
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchContent() {
      setContentLoading(true);
      setError(false);
      try {
        const { data } = await apiClient.get<AboutContentResponse>(
          API_ENDPOINTS.ABOUT_CONTENT,
        );
        if (cancelled) return;

        let content = data.about_content;
        if (roleName === ROLE_RENTER) {
          content = data.about_content_applicant ?? data.about_content;
        } else if (COMPANY_ROLES.includes(roleName)) {
          content = data.about_content_pmc ?? data.about_content;
        }
        setHtmlContent(
          content != null && content !== ""
            ? normalizeHelpAboutHtmlLayout(content)
            : null,
        );
      } catch {
        if (!cancelled) {
          setError(true);
          setHtmlContent(null);
        }
      } finally {
        if (!cancelled) setContentLoading(false);
      }
    }

    fetchContent();
    return () => {
      cancelled = true;
    };
  }, [roleName]);

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900">
      <header
        className="border-b border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5 md:px-8"
        data-testid="applicant-help-page-header"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1
              className="text-xl font-bold text-gray-900 sm:text-2xl"
              data-testid="applicant-help-page-header-title"
            >
              Help / About
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">Resources and support</p>
          </div>
          <div className="flex shrink-0 self-start sm:self-center">
            <ImpersonationStopRow
              variant="pageHeader"
              dataTestId="applicant-help-stop-impersonate-button"
              useApplicantImpersonationFallback
            />
          </div>
        </div>
      </header>

      <div
        data-testid="applicant-help-page"
        className="w-full px-3 sm:px-6 py-4 sm:py-8"
      >
        <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 md:p-8">
          {contentLoading && (
            <div
              className="flex items-center gap-3 text-gray-600"
              data-testid="applicant-help-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span>Loading content…</span>
            </div>
          )}

          {!contentLoading && error && <StaticFallback />}

          {!contentLoading && !error && htmlContent != null && (
            <div
              id="apphelpcontent"
              data-testid="applicant-help-api-content"
              className={HELP_HTML_PROSE}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}

          {!contentLoading && !error && htmlContent == null && <StaticFallback />}
        </div>
      </div>
    </div>
  );
}
